import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const steps = [
  {
    number: "1",
    title: "Login / Sign Up",
    desc: "Create and verify your SynergyCoin account to unlock the dashboard and referral toolkit.",
    image: "images/wallet.png",
    imageAlt: "Wallet illustration",
  },
  {
    number: "2",
    title: "Choose Your Package",
    desc: "Select the presale package that matches your goals and unlocks the discounts you want.",
    image: "images/toy.png",
    imageAlt: "Astronaut illustration",
  },
  {
    number: "3",
    title: "Purchase & Activate Profile",
    desc: "Complete checkout, activate your referral profile, and start earning from every invite.",
    image: "images/synergy.png",
    imageAlt: "SynergyCoin token",
    cta: "Lets Get Started",
  },
];

export default function HowToBuy() {
  const navigate = useNavigate();
  const [selectedStep, setSelectedStep] = useState(null);

  const handleStepClick = (step) => {
    setSelectedStep(step);
  };

  const closeModal = () => setSelectedStep(null);
  const handleGetStarted = () => navigate("/signup");
  const handleBuyClick = () => navigate("/counter");

  useEffect(() => {
    if (!selectedStep) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedStep]);

  return (
    <section className="relative flex w-full min-h-[760px] overflow-hidden bg-gradient-to-b from-black via-[#0a0014] to-black pt-10 pb-8 sm:min-h-[860px] sm:pt-14 sm:pb-10 md:min-h-[940px] md:pt-16 md:pb-10 lg:min-h-[900px] lg:pt-20 xl:min-h-[920px] xl:pb-14">
      <div className="pointer-events-none absolute inset-0">
        <img
          src="images/path.png"
          alt=""
          aria-hidden
          className="absolute inset-x-0 top-28 hidden h-[420px] w-full opacity-80 sm:block lg:top-36 xl:top-44 xl:h-[520px]"
        />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-[90rem] flex-col gap-10 px-5 text-center sm:px-6 md:px-10 lg:px-20 xl:px-28">
        <header className="-mt-6 flex flex-col items-center gap-4 sm:-mt-8 lg:-mt-12 lg:flex-row lg:items-start lg:justify-between lg:text-left">
          <div className="w-full lg:max-w-lg xl:max-w-xl">
            <button
              type="button"
              onClick={handleBuyClick}
              className="inline-flex items-center justify-center rounded-md border border-[#A855F7] bg-transparent px-4 py-2 text-xs text-[#A855F7] transition hover:border-[#c084fc] hover:text-[#c084fc] sm:px-6 sm:text-sm md:text-base"
            >
              BUY
            </button>
            <h2 className="mt-2 text-4xl font-bold uppercase text-white sm:text-3xl md:text-4xl xl:text-5xl">
           How to Buy & Start Referring
            </h2>
          </div>
          <p className="mx-auto max-w-2xl text-sm text-white/70 sm:text-base md:text-lg lg:max-w-xl xl:max-w-3xl xl:text-xl">
            Follow these simple steps to secure your SynergyCoin presale allocation and start tapping into exclusive network rewards.
          </p>
        </header>

        {/* Mobile flow */}
        <div className="flex flex-col gap-5 md:hidden">
          {steps.map((step) => {
            const isCoinStep = step.number === "3";
            const isAstronautStep = step.number === "2";
            const isWalletStep = step.number === "1";
            return (
              <article
                key={step.number}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 text-left shadow-[0_20px_45px_rgba(86,24,188,0.25)] cursor-pointer"
                onClick={() => handleStepClick(step)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleStepClick(step);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#A855F7] to-[#6B21A8] text-lg font-semibold text-white shadow-[0_14px_30px_rgba(86,24,188,0.4)]">
                    {step.number}
                  </span>
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                </div>
                <div className="mt-4 flex flex-col gap-4">
                  <p className="text-sm text-white/70">{step.desc}</p>
                  <div
                    className={`mx-auto ${
                      isAstronautStep || isWalletStep ? "relative w-fit" : ""
                    }`}
                  >
                    {isAstronautStep && (
                      <div className="pointer-events-none absolute inset-x-2 bottom-0 h-8 rounded-full bg-purple-500/25 blur-xl animate-astronaut-pulse" />
                    )}
                    {isWalletStep && (
                      <div className="pointer-events-none absolute inset-x-4 bottom-1 h-7 rounded-full bg-rose-400/25 blur-lg animate-astronaut-pulse" />
                    )}
                    <img
                      src={step.image}
                      alt={step.imageAlt}
                      className={`mx-auto w-auto object-contain ${isCoinStep ? "h-16" : "h-32"} ${
                        isAstronautStep
                          ? "animate-astronaut-drift drop-shadow-[0_20px_48px_rgba(106,48,210,0.4)]"
                          : isWalletStep
                          ? "animate-wallet-bob drop-shadow-[0_16px_40px_rgba(236,72,153,0.3)]"
                          : isCoinStep
                          ? "animate-coin-sway drop-shadow-[0_18px_32px_rgba(140,60,221,0.35)]"
                          : ""
                      }`}
                    />
                  </div>
                  {step.cta && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleGetStarted();
                      }}
                      className="rounded-full bg-gradient-to-r from-[#A855F7] to-[#7C3AED] px-5 py-2 text-sm font-semibold text-white shadow-[0_18px_38px_rgba(120,64,255,0.35)] transition hover:from-[#9c4bff] hover:to-[#6a28e2]"
                    >
                      {step.cta}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* Desktop layout */}
        <div className="hidden flex-col gap-10 md:flex">
          <div className="grid grid-cols-3 items-center gap-8 lg:gap-12">
            <div className="flex justify-center">
              <div className="relative">
                <div className="pointer-events-none absolute inset-x-6 bottom-3 hidden h-10 rounded-full bg-rose-400/20 blur-2xl sm:block animate-astronaut-pulse" />
                <img
                  src={steps[0].image}
                  alt={steps[0].imageAlt}
                  className="h-[220px] w-auto object-contain lg:h-[260px] animate-wallet-bob drop-shadow-[0_24px_60px_rgba(236,72,153,0.28)]"
                />
              </div>
            </div>
            <div
              className="flex flex-col items-start gap-5 text-left md:mb-12 lg:mb-16 cursor-pointer"
              onClick={() => handleStepClick(steps[1])}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-[#A855F7] to-[#6B21A8] text-2xl font-semibold text-white shadow-[0_18px_35px_rgba(104,24,255,0.4)] translate-y-2">
                {steps[1].number}
              </div>
              <h3 className="text-2xl font-semibold text-white">{steps[1].title}</h3>
              <p className="text-base text-white/70">{steps[1].desc}</p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="pointer-events-none absolute inset-x-8 bottom-2 hidden h-10 rounded-full bg-purple-400/20 blur-2xl sm:block animate-astronaut-pulse" />
                <img
                  src={steps[1].image}
                  alt={steps[1].imageAlt}
                  className="h-[240px] w-auto object-contain lg:h-[280px] animate-astronaut-drift"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 items-center gap-8 lg:gap-12">
            <div
              className="flex flex-col items-start gap-5 text-left translate-y-6 cursor-pointer"
              onClick={() => handleStepClick(steps[0])}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-[#A855F7] to-[#6B21A8] text-2xl font-semibold text-white shadow-[0_18px_35px_rgba(104,24,255,0.4)]">
                {steps[0].number}
              </div>
              <h3 className="text-2xl font-semibold text-white">{steps[0].title}</h3>
              <p className="text-base text-white/70">{steps[0].desc}</p>
            </div>
            <div className="flex justify-center -mt-48 lg:-mt-56">
              <img
                src={steps[2].image}
                alt={steps[2].imageAlt}
                className="h-[120px] w-auto object-contain lg:h-[160px] animate-coin-sway drop-shadow-[0_22px_45px_rgba(140,60,221,0.3)]"
              />
            </div>
            <div
              className="flex flex-col items-start gap-5 text-left translate-y-8 lg:translate-x-6 cursor-pointer"
              onClick={() => handleStepClick(steps[2])}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-[#A855F7] to-[#6B21A8] text-2xl font-semibold text-white shadow-[0_18px_35px_rgba(104,24,255,0.4)]">
                {steps[2].number}
              </div>
              <h3 className="text-2xl font-semibold text-white">{steps[2].title}</h3>
              <p className="text-base text-white/70">{steps[2].desc}</p>
              <button
                type="button"
                onClick={handleGetStarted}
                className="rounded-md bg-gradient-to-r from-[#A855F7] to-[#7C3AED] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_18px_38px_rgba(120,64,255,0.35)] transition hover:from-[#9c4bff] hover:to-[#6a28e2]"
              >
                {steps[2].cta}
              </button>
            </div>
          </div>
        </div>
      </div>
      {selectedStep && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-white/20 bg-gradient-to-b from-[#1b0c2b] to-[#0f0718] p-6 text-left shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="step-modal-title"
            aria-describedby="step-modal-description"
          >
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-white/10 p-1 text-white transition hover:bg-white/20"
              aria-label="Close"
            >
              ×
            </button>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#A855F7] to-[#6B21A8] text-lg font-semibold text-white shadow-[0_14px_30px_rgba(86,24,188,0.4)]">
                {selectedStep.number}
              </span>
              <h3 id="step-modal-title" className="text-xl font-semibold text-white">
                {selectedStep.title}
              </h3>
            </div>
            <p id="step-modal-description" className="mt-4 text-sm text-white/80">
              {selectedStep.desc}
            </p>
            {selectedStep.cta && (
              <button
                type="button"
                onClick={handleGetStarted}
                className="mt-6 w-full rounded-full bg-gradient-to-r from-[#A855F7] to-[#7C3AED] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_18px_38px_rgba(120,64,255,0.35)] transition hover:from-[#9c4bff] hover:to-[#6a28e2]"
              >
                {selectedStep.cta}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
